// permissions.js — Centralized Role & Permissions System
//
// Role hierarchy (highest to lowest):
//   owner > director > admin > instructor > staff
//
// Usage:
//   import { hasMinRole, canAccess } from "./permissions";
//   if (hasMinRole(userRole, "admin")) { ... }
//   if (canAccess(userRole, "scheduling", "manage")) { ... }

/* =====================================================
   ROLE DEFINITIONS
   ===================================================== */

export const ROLES = {
  OWNER: "owner",
  DIRECTOR: "director",
  ADMIN: "admin",
  INSTRUCTOR: "instructor",
  STAFF: "staff",
};

export const ROLE_LEVEL = {
  owner: 50,
  director: 40,
  admin: 30,
  instructor: 20,
  staff: 10,
  // Special roles (not in staff hierarchy)
  judge: 5,
  student: 1,
  // Legacy mapping
  front_desk: 10,
};

export const ROLE_LABELS = {
  owner: "Owner",
  director: "Director",
  admin: "Admin",
  instructor: "Instructor",
  staff: "Staff",
  // Special roles
  judge: "Judge",
  student: "Student",
  // Legacy
  front_desk: "Staff",
};

// All roles in order (for dropdowns, etc.)
export const ROLE_LIST = ["owner", "director", "admin", "instructor", "staff"];

/* =====================================================
   ROLE CHECKS
   ===================================================== */

// Is userRole at least as high as minRole?
export function hasMinRole(userRole, minRole) {
  return (ROLE_LEVEL[userRole] || 0) >= (ROLE_LEVEL[minRole] || 0);
}

// Backward compat: equivalent to old isAdmin check (admin, director, or owner)
export function isAdminOrAbove(role) {
  return hasMinRole(role, "admin");
}

// Can this role manage the target role? (e.g. can admin change instructor?)
// Rule: you can only manage roles below your own level
// Special: nobody can delete/demote owner
export function canManageRole(actorRole, targetRole) {
  if (targetRole === "owner") return false; // nobody touches owner
  return (ROLE_LEVEL[actorRole] || 0) > (ROLE_LEVEL[targetRole] || 0);
}

// Is this a portal-only role? (student/parent accounts)
export function isPortalUser(role) {
  return role === "student";
}

// What roles can this actor assign? (roles below their level)
export function assignableRoles(actorRole) {
  const actorLevel = ROLE_LEVEL[actorRole] || 0;
  return ROLE_LIST.filter((r) => ROLE_LEVEL[r] < actorLevel);
}

/* =====================================================
   FEATURE PERMISSIONS
   ===================================================== */

// Each feature maps action names to the minimum required role.
// "view" = can see the page/data
// "manage" = can create/edit/delete
// "full" = unrestricted access

export const PERMISSIONS = {
  dashboard: {
    view: "staff",
  },
  calendar: {
    view: "staff",
  },
  announcements: {
    view: "staff",
    manage: "admin",
  },
  attendance: {
    view: "staff",
    checkin: "instructor",
    manage: "admin",
  },
  classPlanner: {
    view: "staff",
    manage: "instructor", // instructors can edit class plans
  },
  testings: {
    view: "staff",
    score: "instructor",
    manage: "admin",
  },
  tournaments: {
    view: "staff",
    manage: "instructor",
  },
  scheduling: {
    viewOwn: "staff",       // see own schedule
    manage: "admin",        // edit any schedule
    full: "director",       // publish, settings, etc.
  },
  students: {
    view: "instructor",
    manage: "admin",
  },
  users: {
    manage: "director",
  },
  drillApprovals: {
    manage: "director",
  },
};

// Check if userRole can perform action on feature
export function canAccess(userRole, feature, action = "view") {
  const featurePerms = PERMISSIONS[feature];
  if (!featurePerms) return false;
  const minRole = featurePerms[action];
  if (!minRole) return false;
  return hasMinRole(userRole, minRole);
}

/* =====================================================
   SIDEBAR NAV CONFIG
   ===================================================== */

// Minimum role to SEE the sidebar item
// (finer-grained manage/edit checks happen within each page)
export const NAV_ACCESS = {
  dashboard: "staff",
  "black-belt": "instructor",
  reports: "admin",
  "lesson-settings": "admin",
  calendar: "staff",
  announcements: "staff",
  messages: "staff",
  texting: "staff",
  attendance: "staff",
  rosters: "admin",         // event rosters — admin managed
  events: "admin",          // event signups — admin managed
  "class-planner": "staff",
  "video-library": "admin",
  "class-schedule": "admin",
  testings: "staff",
  tournaments: "staff",
  scheduling: "staff",      // everyone sees their own schedule (payroll check in Sidebar)
  "pro-shop": "admin",      // POS terminal — admin only
  campaigns: "admin",       // Campaign tracker — admin only
  students: "instructor",   // instructors can view, admins can edit
  users: "director",
  "drill-approvals": "director",
  business: "admin",
  coupons: "admin",
  tags: "admin",
};